import { useState, useEffect } from "react";
import { X, ShoppingBag, Heart, Star, Shield, Truck, Eye, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import { useLocale } from "../contexts/LocaleContext";

export default function QuickViewModal({ productId, onClose }) {
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const { add } = useCart();
  const { has, toggle } = useWishlist();
  const { fmt } = useLocale();

  useEffect(() => {
    if (!productId) return;
    api.get(`/products/${productId}`).then(r => { setP(r.data); setActive(0); });
  }, [productId]);

  if (!productId) return null;

  return (
    <div data-testid="quickview-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={onClose}>
      <div className="bg-white border border-black/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button data-testid="quickview-close" onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 bg-white border border-black/10 flex items-center justify-center text-gray-700 hover:text-dark hover:border-copper">
          <X size={16}/>
        </button>
        {!p ? (
          <div className="p-20 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8 p-6 md:p-8">
            <div>
              <div className="aspect-square bg-[#FAF7F2] overflow-hidden mb-3">
                <img src={p.images?.[active]} alt={p.name} className="w-full h-full object-cover"/>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {p.images?.slice(0, 4).map((im, i) => (
                  <button key={i} onClick={()=>setActive(i)} className={`aspect-square overflow-hidden border ${active===i? "border-copper":"border-black/10"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-2">{p.brand}</div>
              <h2 className="font-display text-3xl text-dark">{p.name}</h2>
              {p.rating_count > 0 && (
                <div className="flex items-center gap-1 text-sm text-gray-600 mt-2">
                  <Star size={14} className="fill-copper text-copper"/> {p.rating_avg} <span className="text-gray-400">({p.rating_count})</span>
                </div>
              )}
              <div className="mt-5 flex items-baseline gap-3">
                <div className="font-display text-2xl text-dark">{fmt(p.price)}</div>
                {p.original_price && p.original_price > p.price && <div className="text-gray-400 line-through text-base">{fmt(p.original_price)}</div>}
              </div>
              <p className="text-gray-700 mt-5 leading-relaxed text-sm line-clamp-2">{p.description}</p>
              <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-black/5 text-xs">
                <div><div className="text-gray-400 uppercase tracking-luxe">Movement</div><div className="text-gray-900 mt-1">{p.movement || "—"}</div></div>
                <div><div className="text-gray-400 uppercase tracking-luxe">Case</div><div className="text-gray-900 mt-1">{p.case_size || "—"}</div></div>
                <div><div className="text-gray-400 uppercase tracking-luxe">Material</div><div className="text-gray-900 mt-1">{p.case_material || "—"}</div></div>
                <div><div className="text-gray-400 uppercase tracking-luxe">Stock</div><div className={p.stock>0? "text-green-600 mt-1":"text-red-600 mt-1"}>{p.stock>0? `${p.stock} available`:"Out of stock"}</div></div>
              </div>
              <div className="mt-6 flex gap-2 items-stretch">
                <button data-testid="qv-add-cart" onClick={()=>{ add(p); toast.success("Added to cart"); }} className="btn-primary flex-1 justify-center"><ShoppingBag size={13}/> Add to Cart</button>
                <button data-testid="qv-wishlist" onClick={()=>toggle(p)} className={`px-4 border ${has(p.id)?"border-red-500 text-red-500":"border-black/15 text-gray-600 hover:border-dark hover:text-dark"}`}><Heart size={15} fill={has(p.id)?"currentColor":"none"}/></button>
              </div>
              <Link to={`/product/${p.id}`} onClick={onClose} className="inline-flex items-center gap-1 mt-4 text-xs uppercase tracking-luxe text-copper hover:text-[#A0834F]">View full details <ArrowRight size={12}/></Link>
              <div className="mt-5 text-xs text-gray-500 flex items-center gap-4 flex-wrap pt-4 border-t border-black/5">
                <span className="flex items-center gap-1.5"><Shield size={12}/> Authenticated</span>
                <span className="flex items-center gap-1.5"><Truck size={12}/> Insured shipping</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
